import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, FormGroup, InputGroup, Modal } from 'react-bootstrap';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import styled from '@emotion/styled';
import { IoLinkOutline } from 'react-icons/io5';
import * as R from 'ramda';
import copyToClipboard from 'App/utils/copyToClipboard';
import useAddGeneratedMobileLinkMutation from 'App/hooks/useAddGeneratedMobileLinkMutation';
import LaddaButton from 'App/components/LaddaButton';

const propTypes = {
    onHide: PropTypes.func.isRequired,
    patientName: PropTypes.string.isRequired,
    activityName: PropTypes.string.isRequired,
    patientId: PropTypes.string.isRequired,
    activityId: PropTypes.string.isRequired,
};

const defaultProps = {};

const CopyLinkButton = styled(Button)`
    color: var(--biscay);
    background-color: var(--snow);

    &:disabled {
        color: var(--manatee);
        background-color: var(--snow);
        border-color: var(--silver);
    }
`;

function GenerateMobileLinkModal({ onHide, patientName, activityName, patientId, activityId }) {
    const generateLinkMutation = useAddGeneratedMobileLinkMutation(patientId, activityId);

    const mobileLink = R.pathOr('', ['data', 'mobileLink'], generateLinkMutation);

    const handleCopyLink = useCallback(() => {
        copyToClipboard(mobileLink);
    }, [mobileLink]);

    return (
        <Modal onHide={onHide} backdrop="static" show>
            <Modal.Header onHide={onHide}>
                <h4>Generate Mobile Link</h4>
            </Modal.Header>
            <Modal.Body>
                <p>
                    This action will generate a link for
                    <strong>{` ${patientName} `}</strong>
                    for their <strong>{` ${activityName} `}</strong>
                </p>

                <br />

                <p>
                    <strong>Are you sure you want to generate this link?</strong>
                </p>

                <FormGroup>
                    <InputGroup>
                        <FormControl
                            aria-label="Generated Link"
                            type="text"
                            value={mobileLink}
                            readOnly
                            placeholder="No Link Generated"
                        />
                        <InputGroup.Append>
                            <CopyLinkButton
                                type="button"
                                onClick={handleCopyLink}
                                disabled={generateLinkMutation.isLoading || !mobileLink}
                            >
                                <IoLinkOutline /> Copy Link
                            </CopyLinkButton>
                        </InputGroup.Append>
                    </InputGroup>
                </FormGroup>
            </Modal.Body>

            <Modal.Footer>
                <FormButtonGroup>
                    <Button variant="outline-secondary" type="button" onClick={onHide}>
                        Close
                    </Button>
                    <LaddaButton
                        isLoading={generateLinkMutation.isLoading}
                        variant="primary"
                        type="button"
                        disabled={!!mobileLink}
                        onClick={() => generateLinkMutation.mutateAsync()}
                    >
                        Generate
                    </LaddaButton>
                </FormButtonGroup>
            </Modal.Footer>
        </Modal>
    );
}

GenerateMobileLinkModal.propTypes = propTypes;
GenerateMobileLinkModal.defaultProps = defaultProps;

export default GenerateMobileLinkModal;
